import BLOCK_EDITOR_REGISTRY from '../../exercise-templates/block-editors/registry';
import { getBlockType } from '../../../variables/exerciseBlockTypes';
import { MultipleChoicePreview, TrueFalsePreview, FillBlankPreview, OrderingPreview, MatchingPreview, EssayPreview, AudioResponsePreview, SentenceOrderPreview, ErrorSpottingPreview } from './interactivePreviews';
import EmbedConversationPreview from './EmbedConversationPreview';
import EmbedGridPreview from './EmbedGridPreview';
import { BlockTypeBadge } from './blockTypeIcons';
import BlurredPreviewWrapper from './BlurredPreviewWrapper';

const INTERACTIVE_COMPONENTS = {
    multiple_choice: MultipleChoicePreview,
    true_false: TrueFalsePreview,
    fill_blank: FillBlankPreview,
    ordering: OrderingPreview,
    matching: MatchingPreview,
    essay: EssayPreview,
    audio_response: AudioResponsePreview,
    sentence_order: SentenceOrderPreview,
    error_spotting: ErrorSpottingPreview,
};

// Same static-vs-interactive split BlockCard.jsx's builder side already
// makes (BLOCK_KIND_STATIC) - these come from two separate backend entities
// (ExerciseTemplateElement vs ExerciseTemplateInteractiveElement), so the
// blur-reveal request needs to know which one block.id actually refers to.
const INTERACTIVE_TYPE_SET = new Set([...Object.keys(INTERACTIVE_COMPONENTS), 'embed_conversation', 'embed_grid']);

function renderContent(block) {
    if (block.type === 'embed_conversation') return <EmbedConversationPreview block={block} />;
    if (block.type === 'embed_grid') return <EmbedGridPreview block={block} />;
    const InteractiveComponent = INTERACTIVE_COMPONENTS[block.type];
    if (InteractiveComponent) return <InteractiveComponent block={block} />;
    const registryEntry = BLOCK_EDITOR_REGISTRY[block.type];
    return registryEntry ? registryEntry.renderPreview(block) : null;
}

// Static types (heading/text/list/image/audio/video) reuse
// BLOCK_EDITOR_REGISTRY's existing renderPreview as-is (already pure
// functions, already correct). Both embed types get their own dedicated,
// genuinely-interactive component instead of the registry's "📋/🎮
// name"-only placeholder, since each needs to fetch and render its own
// content (a conversation's node graph / a grid's cards). The 5
// "answerable" interactive types get dedicated preview components too
// (interactivePreviews.jsx). Every block is one node on the topic's
// timeline (icon circle + connecting line, see course-preview.css) rather
// than a bordered card - no box, just the accent-colored node marking what
// kind of activity this is.
export default function ExercisePreviewBlock({ block }) {
    const blockType = getBlockType(block.type) || {};
    const content = renderContent(block);
    if (!content) return null;
    const elementType = INTERACTIVE_TYPE_SET.has(block.type) ? 'EXERCISE_INTERACTIVE' : 'EXERCISE';
    return (
        <div className='course-preview-block'>
            <BlockTypeBadge type={block.type} accent={blockType.accent} label={blockType.label} />
            <div className='course-preview-block-content'>
                <BlurredPreviewWrapper elementType={elementType} elementId={block.id} blurEnabled={!!block.blurEnabled}>
                    {content}
                </BlurredPreviewWrapper>
            </div>
        </div>
    );
}
